import {
  createLimitedMapCache,
  LimitedMapCache,
} from 'shared/utils/limitedCache';
import { DocumentUrlsModel } from 'shared/domain/document/documentUrlsModel';
import { DocumentUrlsResult } from 'shared/domain/document/documentUrlsModel';

const MAX_SHARED_PREVIEW_CACHE_ENTRIES = 120;
const SHARED_PREVIEW_CACHE_TTL_MS = 10 * 60 * 1000;
const SHARED_URL_REQUEST_CACHE_TTL_MS = 3 * 60 * 1000;
const PDF_URL_RETRY_COOLDOWN_MS = 5_000;

const pdfUrlRetryAttemptAt = new Map<number, number>();

type CachedPreviewState = {
  signedRequest?: DocumentUrlsModel;
  downloadSrc?: string;
  thumbnail?: string;
  src?: string;
};

/**
 * Shared request cache for resolving signed URLs.
 *
 * Key: `localId`
 * Value: in-flight/resolved `Promise<DocumentUrlsResult>`
 *
 * Why: prevents duplicate `resolveDocumentUrlsWithRetry(...)` calls when
 * multiple uploader instances/tiles ask for the same file at the same time.
 *
 * Cleanup: bounded by `MAX_SHARED_PREVIEW_CACHE_ENTRIES` via
 * `setLimitedMapEntry` (oldest key is evicted when limit is reached).
 */
const sharedDocumentUrlsRequestCache =
  createLimitedMapCache<number, Promise<DocumentUrlsResult>>({
    maxEntries: MAX_SHARED_PREVIEW_CACHE_ENTRIES,
    ttlMs: SHARED_URL_REQUEST_CACHE_TTL_MS,
    refreshTtlOnAccess: false,
  });

/**
 * Shared resolved preview cache.
 *
 * Keys: `remote:<_id>` and/or `local:<localId>`
 * Value: already resolved preview payload (`signedRequest`, `downloadSrc`, etc.)
 *
 * Why: allows reusing preview state after component remount/navigation to
 * reduce spinner reappearance and repeated URL resolution work.
 *
 * Cleanup: bounded by `MAX_SHARED_PREVIEW_CACHE_ENTRIES` via
 * `setLimitedMapEntry` (oldest key is evicted when limit is reached).
 */
const sharedResolvedPreviewCache =
  createLimitedMapCache<string, CachedPreviewState>({
    maxEntries: MAX_SHARED_PREVIEW_CACHE_ENTRIES,
    ttlMs: SHARED_PREVIEW_CACHE_TTL_MS,
  });

function toCacheKeys(file: { _id?: string; localId?: number }): string[] {
  const keys: string[] = [];

  if (typeof file._id === 'string' && file._id.length > 0) {
    keys.push(`remote:${file._id}`);
  }

  if (typeof file.localId === 'number' && Number.isFinite(file.localId)) {
    keys.push(`local:${file.localId}`);
  }

  return keys;
}

export function getSharedDocumentUrlsRequestCache(): LimitedMapCache<
  number,
  Promise<DocumentUrlsResult>
> {
  return sharedDocumentUrlsRequestCache;
}

export function setSharedDocumentUrlsRequestCacheEntry(
  localId: number,
  request: Promise<DocumentUrlsResult>
): void {
  sharedDocumentUrlsRequestCache.set(localId, request);
}

export function cacheResolvedPreviewState(
  file: { _id?: string; localId?: number },
  state: CachedPreviewState
): void {
  toCacheKeys(file).forEach((key) => {
    const existing = sharedResolvedPreviewCache.get(key);
    sharedResolvedPreviewCache.set(key, {
      ...existing,
      ...state,
    });
  });
}

export function getCachedResolvedPreviewState(
  file: { _id?: string; localId?: number }
): CachedPreviewState | undefined {
  const keys = toCacheKeys(file);

  for (const key of keys) {
    const cached = sharedResolvedPreviewCache.get(key);
    if (cached) {
      return cached;
    }
  }

  return undefined;
}

export function clearDocumentUrlsRequestCache(localId: number): void {
  sharedDocumentUrlsRequestCache.delete(localId);
  pdfUrlRetryAttemptAt.delete(localId);
}

export function clearDocumentPreviewCache(
  file: { _id?: string; localId?: number }
): void {
  if (typeof file.localId === 'number' && Number.isFinite(file.localId)) {
    clearDocumentUrlsRequestCache(file.localId);
  }

  toCacheKeys(file).forEach((key) => {
    sharedResolvedPreviewCache.delete(key);
  });
}

export function clearDocumentPreviewCachesForDocuments(
  documents: Array<{ _id?: string; localId?: number }>
): void {
  documents.forEach(clearDocumentPreviewCache);
}

/** Avoid hammering getSources when PDF conversion is still pending. */
export function shouldSkipPdfUrlRetry(localId: number): boolean {
  const lastAttemptAt = pdfUrlRetryAttemptAt.get(localId);
  if (lastAttemptAt === undefined) {
    return false;
  }
  return Date.now() - lastAttemptAt < PDF_URL_RETRY_COOLDOWN_MS;
}

export function markPdfUrlRetryAttempt(localId: number): void {
  pdfUrlRetryAttemptAt.set(localId, Date.now());
}
